import type { ErrorCode } from "@bridewell/shared";

/** Thrown for every non-2xx response; mirrors the API's `{ error }` envelope. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: ErrorCode | string,
    message: string,
    public readonly details?: unknown,
    /** The API's `x-request-id` (Cloudflare's ray id): shown with a server error so a person can quote it to support. */
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
  /** Free-plan item cap (iOS parity). */
  get isFreeLimit() {
    return this.code === "FREE_LIMIT_REACHED";
  }
  /** Any ceiling that an upgrade (or archiving) lifts: free item caps and paid-tier limits alike. */
  get isPlanLimit() {
    return this.code === "FREE_LIMIT_REACHED" || this.code === "PLAN_LIMIT_REACHED";
  }
  /** The feature lives on a higher tier (Pro templates, hand-off, planner view). */
  get isPlanRequired() {
    return this.code === "PLAN_REQUIRED";
  }
}

/**
 * A Better Auth client error (`{ status, code, message }`, e.g. a wrong current password) as an `ApiError`: an answer of
 * the API like any other, shown to the person and never reported as a bug (`isReportableError`).
 */
export function authApiError(e: { status?: number; code?: string; message?: string; statusText?: string }): ApiError {
  return new ApiError(e.status ?? 0, e.code ?? "AUTH_ERROR", e.message || e.statusText || "Request failed");
}

/**
 * The request got no answer at all: no connection, server unreachable, DNS or TLS failure. Every engine words that
 * differently (the browser's `TypeError: Failed to fetch`, Expo's `Error: fetch failed: java.net.ConnectException…`),
 * so the transport reports all of them as this one type and a screen can say "you're offline" instead.
 */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super("Network request failed", { cause });
    this.name = "NetworkError";
  }
}

interface Envelope<T> {
  data: T;
  meta?: Record<string, unknown>;
  access?: unknown;
}

/**
 * How a platform reaches the API. The web is same-origin and cookie-authenticated (the defaults); the
 * Android app sets `baseUrl` to the API origin, `credentials: "omit"` (React Native's own cookie jar must
 * not interfere) and a `headers` provider that attaches the Better Auth cookie it keeps in secure storage.
 */
export interface ApiConfig {
  /** API origin without a trailing slash. "" = the page's own origin (the web). */
  baseUrl: string;
  credentials: RequestCredentials;
  /** Extra headers for every request, read at call time (so a fresh sign-in is picked up without re-configuring). May be async (secure storage). */
  headers?: () => Record<string, string> | undefined | Promise<Record<string, string> | undefined>;
}

let config: ApiConfig = { baseUrl: "", credentials: "same-origin" };

/** Merge platform settings over the defaults. Call once at start-up, before the first request. */
export function configureApi(next: Partial<ApiConfig>) {
  config = { ...config, ...next, baseUrl: (next.baseUrl ?? config.baseUrl).replace(/\/+$/, "") };
}

/** Absolute (or origin-relative on the web) URL of an API path; `/weddings` and `/api/weddings` are the same path. */
export function apiUrl(path: string): string {
  return `${config.baseUrl}${path.startsWith("/api") ? path : `/api${path}`}`;
}

/**
 * The single HTTP entry point for every client. JSON in/out, the `{ data, meta, access }` envelope back,
 * `ApiError` for every non-2xx. Components never call `fetch` directly.
 */
export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<Envelope<T>> {
  const { json, headers, ...rest } = init;
  const platformHeaders = (await config.headers?.()) ?? {};
  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      credentials: config.credentials,
      ...rest,
      headers: {
        accept: "application/json",
        ...(json !== undefined ? { "content-type": "application/json" } : {}),
        ...platformHeaders,
        ...(headers ?? {}),
      },
      body: json !== undefined ? JSON.stringify(json) : rest.body,
    });
  } catch (e) {
    // A cancelled request is the caller's decision, not a lost connection.
    if ((e as { name?: unknown } | null)?.name === "AbortError") throw e;
    throw new NetworkError(e);
  }

  if (res.status === 204) return { data: undefined as T };
  let text: string;
  try {
    text = await res.text();
  } catch (error) {
    if ((error as { name?: unknown } | null)?.name === "AbortError") throw error;
    throw new NetworkError(error);
  }
  let body: unknown = undefined;
  try { body = text ? JSON.parse(text) : undefined; } catch { /* non-JSON */ }

  if (!res.ok) {
    const err = (body as { error?: { code?: string; message?: string; details?: unknown } } | undefined)?.error;
    throw new ApiError(res.status, err?.code ?? "INTERNAL", err?.message ?? `Request failed (${res.status})`, err?.details, res.headers.get("x-request-id") ?? undefined);
  }
  if (!body || typeof body !== "object" || !Object.hasOwn(body, "data")) throw new Error("Invalid API response");
  return body as Envelope<T>;
}

export const http = {
  get: <T>(path: string) => api<T>(path),
  post: <T>(path: string, json?: unknown) => api<T>(path, { method: "POST", json }),
  patch: <T>(path: string, json: unknown) => api<T>(path, { method: "PATCH", json }),
  put: <T>(path: string, body: BodyInit) => api<T>(path, { method: "PUT", body }),
  putJson: <T>(path: string, json: unknown) => api<T>(path, { method: "PUT", json }),
  del: <T = void>(path: string) => api<T>(path, { method: "DELETE" }),
  upload: <T>(path: string, form: FormData, method: "POST" | "PUT" = "POST") => api<T>(path, { method, body: form }),
};
